import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import SEOHead from "@/components/base/SEOHead";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const baseUrl = "https://heiliao.wan.com";

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category ? category.name : "全部";
  const categoryDescription = category
    ? `${categoryName}相关的所有黑料爆料和内幕信息`
    : "浏览全部黑料爆料文章";

  const filteredArticles =
    slug === "all" ? articles : articles.filter((a) => a.category === slug);

  const pageTitle = `${categoryName} - 黑料wan网`;
  const pageDesc = `浏览${categoryName}分类下的所有最新黑料爆料文章。黑料wan网为您提供最全面的${categoryName}相关内幕信息，每日更新。`;
  const pageUrl = `${baseUrl}/category/${slug}`;

  const schemaJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: baseUrl },
          { "@type": "ListItem", position: 2, name: categoryName },
        ],
      },
      {
        "@type": "CollectionPage",
        "@id": pageUrl,
        url: pageUrl,
        name: pageTitle,
        description: pageDesc,
        isPartOf: { "@id": `${baseUrl}/#website` },
      },
    ],
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: categoryName },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`黑料wan网,${categoryName},黑料爆料,最新黑料`}
        canonicalUrl={pageUrl}
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12 md:pb-16 bg-background-50 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">{categoryDescription}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                      {article.isHot && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            热门
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                        <span className="whitespace-nowrap">{article.publishDate}</span>
                        <span className="whitespace-nowrap">{article.readTime}阅读</span>
                        <span className="whitespace-nowrap">{article.author}</span>
                      </div>
                      <h2 className="text-foreground-950 font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-foreground-600 text-sm line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-4 mt-4 text-foreground-500 text-xs">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-eye-line"></i>
                          {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
                        </span>
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-message-2-line"></i>
                          {article.commentCount}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}